import { Close, ShoppingCart } from '@mui/icons-material'
import {
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  Fab,
  IconButton,
  MobileStepper,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
  useMediaQuery,
  useTheme
} from '@mui/material'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import 'swiper/css'
import 'swiper/css/navigation'
import '../assets/styles.css'
import CartItem from '../components/CartItem'
import Loading from '../components/Loading'
import MessageManager from '../components/MessageManager'
import { useApp } from '../hooks/useApp'
import { CONSTANT } from '../utils/Constants'
import { formatNumber } from '../utils/commons'
import './../i18n'
import Confirmation from './bookingSteps/Confirmation'
import Information from './bookingSteps/Information'
import Installations from './bookingSteps/Installations'
import Payments from './bookingSteps/Payments'
import Schedules from './bookingSteps/Schedules'
import UserData from './bookingSteps/UserData'
import { usePermissionsAssociated } from '../queries/useCompanyQueries'
import { queryClient } from '../queries/config'
import { setSearchFormDataKey } from '../queries/useSearchState'
import { setFormDataKey, useFormData } from '../queries/userGlobalQueries'

const BtnCart = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 'calc(100vh - 500px)',
  left: 'calc(100vw - 170px)',
  flexDirection: 'row',
  justifyContent: 'center',
  zIndex: 900,
  [theme.breakpoints.down('md')]: {
    top: 'unset',
    left: 'unset',
    bottom: theme.spacing(55),
    right: theme.spacing(3)
  }
}))

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

export type StepsType = {
  label: string
  component: ReactNode
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      sx={{ width: '100%', height: '100%' }}
    >
      {value === index && <>{children}</>}
    </Box>
  )
}

const Bookings = () => {
  const { t } = useTranslation()
  const {
    reserved,
    iDCompany,
    loading,
    successMsg,
    resetSuccessMsg,
    errorMsg,
    resetErrorMsg,
    colegial,
    setColegial,
    cartData,
    setCartData,
    disabledFullday,
    setDisabledFullday,
    payOrder,
    setPayOrder,
    setHaveAssociated,
    activeTab,
    setActiveTab
  } = useApp()

  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [hiddenCart, setHiddenCart] = useState(true)
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)
  const [currency, setCurrency] = useState<string>('USD')

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('md'))

  // Query para permisos de asociados
  const { data: permissionsData } = usePermissionsAssociated()

  const { data: formData } = useFormData()

  useEffect(() => {
    if (permissionsData) {
      setHaveAssociated && setHaveAssociated(permissionsData.hasAssociated)
    }
  }, [permissionsData, setHaveAssociated])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (
      urlParams.has('origen') &&
      urlParams.has('destino') &&
      urlParams.has('date') &&
      urlParams.has('passengers')
    ) {
      const searchData = {
        origen: urlParams.get('origen'),
        destino: urlParams.get('destino'),
        date: urlParams.get('date'),
        passengers: urlParams.get('passengers')
      }
      queryClient.setQueryData([setSearchFormDataKey], searchData)
      queryClient.setQueryData([setFormDataKey], (old: any) => ({
        ...old,
        isBookingFromOtherSite: true,
        areaType: 'transportation',
        area: searchData.origen,
        desc: searchData.destino,
        bookingDay: searchData.date,
        spaces: searchData.passengers
      }))
      if (setActiveTab) setActiveTab(1)
    }
  }, [])

  const handleNext = () => {
    if (activeTab !== 5 && setActiveTab) {
      setActiveTab(activeTab + 1)
    }
  }

  const handleBack = () => {
    if (setActiveTab) {
      setActiveTab(activeTab - 1)
    }
  }

  useEffect(() => {
    if (iDCompany?.body?.content?.[0]?.currencies?.[0]?.symbol) {
      setCurrency(iDCompany.body.content[0].currencies[0].symbol)
    }
  }, [iDCompany])

  const deleteItemCart = (id: number) => {
    let subtractionAmount = cartData.allAmount
    const filter = cartData.allBookings.filter(
      (item: any) => item.bookingDay === cartData.allBookings[id].bookingDay
    )
    if (cartData.allBookings[id].areaType === 2) {
      if (filter.length === 1) {
        subtractionAmount = cartData.allAmount - cartData.allBookings[id].areaAmount
      }
    } else {
      subtractionAmount = cartData.allAmount - cartData.allBookings[id].areaAmount
    }
    const newDataCart = cartData.allBookings.filter((index) => index !== cartData.allBookings[id])
    setCartData && setCartData({ allAmount: subtractionAmount, allBookings: newDataCart })
  }

  const steps: StepsType[] = [
    {
      label: `${t('selectInstallation')}`,
      component: (
        <Installations
          loading={loading}
          handleNext={handleNext}
          isFirstLoad={isFirstLoad}
          setIsFirstLoad={setIsFirstLoad}
        />
      )
    },
    {
      label: `${t('scheduleTheDay')}`,
      component: (
        <Schedules
          handleNext={handleNext}
          handleBack={handleBack}
          setColegial={setColegial}
          colegial={colegial}
          setDisabledFullday={setDisabledFullday}
          disabledFullday={disabledFullday}
          haveAssociated={permissionsData?.hasAssociated}
          currency={currency}
        />
      )
    },
    {
      label: `${t('bookingInformation')}`,
      component: (
        <Information
          handleNext={handleNext}
          handleBack={handleBack}
          setHiddenCart={setHiddenCart}
          setActiveStep={setActiveTab}
          setCartData={setCartData}
          cartData={cartData}
          colegial={colegial}
          currency={currency}
        />
      )
    },
    {
      label: `${t('confirmationOfData')}`,
      component: (
        <UserData
          currency={currency}
          handleNext={handleNext}
          handleBack={handleBack}
          colegial={colegial}
          cartData={cartData}
          setActiveStep={setActiveTab}
          setHiddenCart={setHiddenCart}
          setCartData={setCartData}
        />
      )
    },
    {
      label: `${t('paymentMethod')}`,
      component: (
        <Payments
          currency={currency}
          handleNext={handleNext}
          handleBack={handleBack}
          cartData={cartData}
          colegial={colegial}
          setPayOrder={setPayOrder}
        />
      )
    },
    {
      label: `${t('confirmationAndBooking')}`,
      component: (
        <Confirmation
          currency={currency}
          handleBack={handleBack}
          setActiveStep={setActiveTab}
          colegial={colegial}
          cartData={cartData}
          setCartData={setCartData}
          setColegial={setColegial}
          setHiddenCart={setHiddenCart}
          payOrder={payOrder}
        />
      )
    }
  ]

  return (
    <>
      {loading && <Loading />}
      {errorMsg && (
        <MessageManager
          type={CONSTANT.ERROR_MSG}
          msg={errorMsg}
          details=""
          callback={resetErrorMsg}
          color="primary.main"
        />
      )}
      {successMsg && (
        <MessageManager
          type={CONSTANT.SUCCESS_MSG}
          msg={successMsg}
          details=""
          callback={resetSuccessMsg}
          color="primary.main"
        />
      )}
      <Container
        maxWidth="xl"
        sx={{
          paddingBottom: '0',
          paddingTop: '0',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflowY: 'auto'
        }}
      >
        <Typography variant="h3" align="center" fontWeight={300} mt={2} mb={3}>
          {t('makeYourBooking')}
        </Typography>
        <Box
          sx={{
            pr: { xs: 2, sm: 5 },
            borderRadius: 3,
            flexGrow: 1,
            minHeight: 'calc(100vh - 300px)'
          }}
          elevation={2}
          component={Paper}
          display={{ xs: 'block', sm: 'grid' }}
          gridTemplateColumns={{ xs: '1fr', md: '350px 1fr' }}
        >
          <Box
            component={Stepper}
            orientation="vertical"
            activeStep={activeTab}
            sx={{
              display: { xs: 'none', md: 'flex' },
              borderColor: 'divider',
              alignSelf: 'flex-start',
              marginX: 'auto',
              marginTop: '2rem',
              pb: 2
            }}
          >
            {!reserved &&
              steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>
                    <b>{step.label}</b>
                  </StepLabel>
                </Step>
              ))}
          </Box>

          <MobileStepper
            steps={6}
            activeStep={activeTab}
            variant="progress"
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'flex-start',
              marginX: 'auto',
              height: '50px'
            }}
            nextButton={null}
            backButton={null}
          />

          <Stack sx={{ borderLeft: 1, pl: '1.5rem' }}>
            <CustomTabPanel value={activeTab} index={0}>
              <Installations
                loading={loading}
                handleNext={handleNext}
                isFirstLoad={isFirstLoad}
                setIsFirstLoad={setIsFirstLoad}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={1}>
              <Schedules
                handleNext={handleNext}
                handleBack={handleBack}
                setColegial={setColegial}
                colegial={colegial}
                setDisabledFullday={setDisabledFullday}
                disabledFullday={disabledFullday}
                haveAssociated={permissionsData?.hasAssociated}
                currency={currency}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={2}>
              <Information
                handleNext={handleNext}
                handleBack={handleBack}
                setHiddenCart={setHiddenCart}
                setActiveStep={setActiveTab}
                setCartData={setCartData}
                cartData={cartData}
                colegial={colegial}
                currency={currency}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={3}>
              <UserData
                handleNext={handleNext}
                handleBack={handleBack}
                colegial={colegial}
                cartData={cartData}
                setActiveStep={setActiveTab}
                setHiddenCart={setHiddenCart}
                setCartData={setCartData}
                currency={currency}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={4}>
              <Payments
                handleNext={handleNext}
                handleBack={handleBack}
                cartData={cartData}
                colegial={colegial}
                setPayOrder={setPayOrder}
                currency={currency}
              />
            </CustomTabPanel>
            <CustomTabPanel value={activeTab} index={5}>
              <Confirmation
                handleBack={handleBack}
                setActiveStep={setActiveTab}
                colegial={colegial}
                cartData={cartData}
                setCartData={setCartData}
                setColegial={setColegial}
                setHiddenCart={setHiddenCart}
                payOrder={payOrder}
                currency={currency}
              />
            </CustomTabPanel>
          </Stack>
        </Box>
        <BtnCart
          sx={{
            display: hiddenCart || cartData.allBookings.length === 0 ? 'none' : 'flex'
          }}
        >
          {!openDrawer && (
            <Fab
              color="primary"
              aria-label="Carrito"
              variant="extended"
              onClick={() => setOpenDrawer(true)}
            >
              <ShoppingCart fontSize="large" />
              {isSm ? (
                cartData.allBookings.length
              ) : (
                <Typography variant="caption">{cartData.allBookings.length} Pendiente</Typography>
              )}
            </Fab>
          )}
        </BtnCart>
        <Drawer
          PaperProps={{
            sx: { width: '350px', padding: 2, justifyContent: 'space-between' }
          }}
          elevation={2}
          anchor="right"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
        >
          <Box display="flex" width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
            <Box display="flex" gap={2} alignItems="center">
              <ShoppingCart fontSize="large" color="primary" />
              <Typography variant="body2">{cartData.allBookings.length} Pendiente</Typography>
            </Box>
            <IconButton aria-label="cerrar" onClick={() => setOpenDrawer(false)}>
              <Close color="primary" fontSize="large" />
            </IconButton>
          </Box>
          <Divider />
          <Box flex={1}>
            {cartData.allBookings.map((item: any, i: number) => (
              <CartItem
                currency={iDCompany?.body.currency ?? 'USD'}
                key={i}
                colegial={colegial}
                amount={item.areaAmount}
                date={item.bookingDay}
                areaName={item.areaName}
                logo={item.logo}
                onClick={() => deleteItemCart(i)}
                initHour={item.startDate}
                endHour={item.endDate}
              />
            ))}
          </Box>
          <Button
            sx={{ justifyContent: 'space-between' }}
            disabled={cartData.allBookings.length === 0}
            variant="contained"
            onClick={() => {
              setOpenDrawer(false)
              if (setActiveTab) {
                setActiveTab(colegial.BookingAdmin ? 5 : 3)
              }
            }}
          >
            {colegial.BookingAdmin ? (
              <Box flex={1}>Ir a confirmación</Box>
            ) : (
              <>
                <span>Confirmar reserva</span>
                <span>{formatNumber(cartData.allAmount, currency)}</span>
              </>
            )}
          </Button>
        </Drawer>
      </Container>
    </>
  )
}

export default Bookings
